import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { BaseVideo } from '../types/types'
import { PrimaryBtnBlueShort } from '../../../components/Buttons'
import NextVideoTimer, { AutoplayInfo } from './NextVideoTimer'
import { useTranslation } from 'react-i18next'
import { useVideoPersistence } from '../hooks/useVideoPersistence'
import VideoLayout from './VideoLayout'
import ProgressBlock from './ProgressBlock'
import { Menu } from 'lucide-react'
import { Tooltip } from 'react-tooltip'
import { useLanguages } from '../../../hooks/useLanguages'
import { useVideoFlow } from '../hooks/useVideoFlow'
import Logger from '../../../helpers/logger'

interface VideoPlayerProps {
    video: BaseVideo
    onComplete: () => void
    skippable?: boolean
    autoplayInfo?: AutoplayInfo
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ video, onComplete, skippable = false, autoplayInfo }) => {
    const { t } = useTranslation('onboarding')
    const [progress, setProgress] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [canComplete, setCanComplete] = useState(false)
    const [duration, setDuration] = useState(0)
    const [showNextVideoTimer, setShowNextVideoTimer] = useState(false)
    const [countdown, setCountdown] = useState(5)
    const videoRef = useRef<HTMLVideoElement>(null)
    const countdownRef = useRef<NodeJS.Timeout>()
    const playStateRef = useRef<boolean>(false)
    const { isVideoWatched, setWatchedVideo } = useVideoPersistence()
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false)
    const { userLang } = useLanguages()
    const { resolvedLanguage } = userLang
    const { videoControls, isPlayingVideo } = useVideoFlow()

    const handleCancelAutoplay = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
            countdownRef.current = undefined
        }
        setShowNextVideoTimer(false)
    }

    const handlePlayPause = async () => {
        if (!videoRef.current) return

        if (playStateRef.current) {
            videoRef.current.pause()
        } else {
            try {
                await videoRef.current.play()
            } catch (error) {
                console.error('Play failed:', error)
            }
        }
    }

    const attemptAutoplay = async () => {
        if (!videoRef.current) return
        try {
            await videoRef.current.play()
            videoControls.startPlaying()
        } catch (error) {
            console.log('Autoplay failed:', error)
            videoControls.stopPlaying()
            playStateRef.current = false
        }
    }

    const handleVideoEnd = () => {
        console.log('Video ended:', video.id)
        videoControls.stopPlaying()
        setCanComplete(true)

        setWatchedVideo(video.id)

        if (autoplayInfo?.hasNextVideo) {
            if (countdownRef.current) {
                clearInterval(countdownRef.current)
            }

            setShowNextVideoTimer(true)
            setCountdown(5)

            let timeLeft = 5
            countdownRef.current = setInterval(() => {
                timeLeft -= 1
                setCountdown(timeLeft)

                if (timeLeft <= 0) {
                    clearInterval(countdownRef.current)
                    setShowNextVideoTimer(false)
                    onComplete()
                }
            }, 1000)
        } else {
            onComplete()
        }
    }

    useEffect(() => {
        const checkVideoStatus = () => {
            // Check both the video's completed property and sessionStorage
            const wasWatched = isVideoWatched(video.id)
            console.log(`Video ${video.id} status check:`, {
                completed: video.completed,
                wasWatched,
            })

            if (video.completed || wasWatched) {
                console.log(`Video ${video.id} was previously completed, skipping...`)
                handleVideoEnd()
                return true
            }
            return false
        }

        // Check status immediately when the component mounts or video changes
        if (checkVideoStatus()) {
            return // Skip further setup if video was already watched
        }

        const videoElement = videoRef.current
        if (!videoElement) return

        console.log('Setting up video event listeners for video:', video.id)

        const handleLoadedMetadata = () => {
            console.log('Video metadata loaded')
            setDuration(videoElement.duration)
            attemptAutoplay()
        }

        const handleTimeUpdate = () => {
            if (!videoRef.current) return
            const videoElement = videoRef.current
            const currentProgress = (videoElement.currentTime / videoElement.duration) * 100

            // Add detailed logging for video progress
            // console.log('Video progress:', {
            //     videoId: video.id, // This will now have the current video.id
            //     videoType: video.type,
            //     progress: currentProgress,
            //     currentTime: videoElement.currentTime,
            //     duration: videoElement.duration,
            //     canComplete,
            //     playStateRef: playStateRef.current,
            //     isPlayingVideo,
            // })

            setProgress(currentProgress)
            setCurrentTime(videoElement.currentTime)

            if (currentProgress >= 99 && !canComplete) {
                setCanComplete(true)
            }
        }

        const handlePlay = () => {
            playStateRef.current = true
            videoControls.startPlaying()
            Logger.debug('VideoPlayer', 'Play triggered:', {
                videoId: video.id,
                playStateRef: playStateRef.current,
                currentTime: videoRef.current?.currentTime,
            })
        }

        const handlePause = () => {
            playStateRef.current = false
            videoControls.stopPlaying()
            Logger.debug('VideoPlayer', 'Pause triggered:', {
                videoId: video.id,
                playStateRef: playStateRef.current,
                currentTime: videoRef.current?.currentTime,
            })
        }

        // Add event listeners
        videoElement.addEventListener('loadedmetadata', handleLoadedMetadata)
        videoElement.addEventListener('timeupdate', handleTimeUpdate)
        videoElement.addEventListener('play', handlePlay)
        videoElement.addEventListener('pause', handlePause)
        videoElement.addEventListener('ended', handleVideoEnd)

        // Cleanup function
        return () => {
            console.log('Cleaning up video event listeners')
            videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
            videoElement.removeEventListener('timeupdate', handleTimeUpdate)
            videoElement.removeEventListener('play', handlePlay)
            videoElement.removeEventListener('pause', handlePause)
            videoElement.removeEventListener('ended', handleVideoEnd)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video.id, video.type, canComplete, isPlayingVideo])

    useEffect(() => {
        if (videoRef.current) {
            const isActuallyPlaying = !videoRef.current.paused
            if (isActuallyPlaying !== isPlayingVideo) {
                Logger.debug('VideoPlayer', 'Syncing play states:', {
                    videoId: video.id,
                    htmlVideoPlaying: isActuallyPlaying,
                    statePlayingVideo: isPlayingVideo,
                })
                if (isActuallyPlaying) {
                    videoControls.startPlaying()
                } else {
                    videoControls.stopPlaying()
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlayingVideo, video.id])

    // Reset function updated to include controls state
    useEffect(() => {
        setProgress(0)
        setCurrentTime(0)
        setCanComplete(false)
        videoControls.stopPlaying()
        playStateRef.current = false
        setShowNextVideoTimer(false)
        setCountdown(5)

        if (countdownRef.current) {
            clearInterval(countdownRef.current)
            countdownRef.current = undefined
        }

        if (videoRef.current) {
            videoRef.current.currentTime = 0
            attemptAutoplay()
        }

        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current)
                countdownRef.current = undefined
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video.id, video.type])

    const handleManualComplete = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
        }
        setShowNextVideoTimer(false)
        onComplete()
    }

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    return (
        <VideoLayout currentVideoId={video.id}>
            <ProgressBlock currentVideoId={video.id} isCollapsed={isMenuCollapsed} />
            <PlayerContainer>
                <VideoTitle>
                    {typeof video.title === 'object' && resolvedLanguage
                        ? video.title.lang[resolvedLanguage as keyof typeof video.title.lang]
                        : typeof video.title === 'object'
                        ? video.title.lang.en
                        : video.title}
                </VideoTitle>
                <VideoWrapper>
                    <MenuButton
                        onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
                        data-tooltip-id="menu-tooltip"
                        data-tooltip-content={isMenuCollapsed ? 'Show menu' : 'Hide menu'}
                    >
                        <Menu size={18} />
                    </MenuButton>
                    <Tooltip id="menu-tooltip" />
                    <video ref={videoRef} width="100%" height="100%" controls={false} src={video.url} playsInline />
                    <ControlButton
                        className="control-button"
                        onClick={handlePlayPause}
                        aria-label={isPlayingVideo ? 'Pause video' : 'Play video'}
                        data-playing={isPlayingVideo}
                    />
                    {showNextVideoTimer && autoplayInfo?.hasNextVideo && (
                        <NextVideoTimer
                            seconds={countdown}
                            nextVideoTitle={autoplayInfo.nextVideoTitle}
                            onCancel={handleCancelAutoplay}
                        />
                    )}
                </VideoWrapper>

                <ProgressBar>
                    <Progress width={progress} />
                </ProgressBar>

                <Controls>
                    <TimeDisplay>
                        {t('videoPlayer.timeFormat', {
                            current: formatTime(currentTime),
                            total: formatTime(duration),
                        })}
                    </TimeDisplay>

                    <PrimaryBtnBlueShort onClick={handleManualComplete} disabled={!canComplete}>
                        {canComplete ? t('videoPlayer.actions.continue') : t('videoPlayer.actions.watchMore')}
                    </PrimaryBtnBlueShort>
                </Controls>
            </PlayerContainer>
        </VideoLayout>
    )
}

export default VideoPlayer

const MenuButton = styled.button`
    position: absolute;
    top: 1rem;
    left: 1rem;
    width: 32px;
    height: 32px;
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    z-index: 10;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }

    @media (max-width: 768px) {
        display: none;
    }
`

export const PlayerContainer = styled.div`
    width: 70%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
`

export const VideoWrapper = styled.div`
    width: 100%;
    position: relative;
    aspect-ratio: 16/9;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    &:hover .control-button {
        opacity: 1;
    }
`

const PlayButton = styled.button`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }

    &::before {
        content: '';
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 15px 0 15px 25px;
        border-color: transparent transparent transparent white;
        margin-left: 5px;
    }
`

interface ControlButtonProps {
    'data-playing'?: boolean
}

const ControlButton = styled(PlayButton)<ControlButtonProps>`
    opacity: 0;
    transition: opacity 0.3s;

    &::before {
        content: '';
        width: ${props => (props['data-playing'] ? '20px' : '0')};
        height: ${props => (props['data-playing'] ? '30px' : '0')};
        border-style: ${props => (props['data-playing'] ? 'double' : 'solid')};
        border-width: ${props => (props['data-playing'] ? '0 0 0 20px' : '15px 0 15px 25px')};
        border-color: ${props => (props['data-playing'] ? 'white' : 'transparent transparent transparent white')};
        margin-left: ${props => (props['data-playing'] ? '0' : '5px')};
    }
`

const VideoTitle = styled.h2`
    font-size: 1.5rem;
    color: #999;
    margin-bottom: 30px;
`

const ProgressBar = styled.div`
    width: 100%;
    height: 4px;
    background: #ddd;
    margin-top: 1rem;
    border-radius: 2px;
`

const Progress = styled.div<{ width: number }>`
    width: ${props => (isNaN(props.width) ? 0 : props.width)}%;
    height: 100%;
    background: #007bff;
    border-radius: 2px;
    transition: width 0.3s ease;
`

const Controls = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-top: 1rem;
`

const TimeDisplay = styled.span`
    font-size: 0.9rem;
    color: #666;
`
